import {
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  PartyPopper,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "../../components/layout/SiteLayout";
import { focusRing } from "../../components/ui/focusRing";
import { API_BASE_URL } from "../../config";

type WelcomeStatus = "success" | "reinstall" | "trial_started" | "cancelled" | "error";

function Welcome() {
  const [searchParams] = useSearchParams();

  const status = (searchParams.get("status") as WelcomeStatus) || "success";
  const team = searchParams.get("team") || "your workspace";
  const appId = searchParams.get("app_id") || "";
  const error = searchParams.get("error") || "Unknown error";
  const trialDays = parseInt(searchParams.get("trial_days") || "30", 10);
  const hasPaidAccess = searchParams.get("paid") === "true";
  const teamId = searchParams.get("team_id") || "";
  const preferAnnual = searchParams.get("plan") === "annual";

  return (
    <SiteLayout navVariant="inner" footerVariant="home">
      <main className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
        {status === "error" ? (
          <ErrorContent error={error} />
        ) : status === "cancelled" ? (
          <CancelledContent />
        ) : (
          <SuccessContent
            status={status}
            team={team}
            appId={appId}
            trialDays={trialDays}
            hasPaidAccess={hasPaidAccess}
            teamId={teamId}
            preferAnnual={preferAnnual}
          />
        )}
      </main>
    </SiteLayout>
  );
}

function ErrorContent({ error }: { error: string }) {
  return (
    <div className="max-w-md w-full bg-white rounded-3xl border border-orange-200 shadow-sm p-8 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <XCircle className="w-8 h-8 text-red-600" />
      </div>
      <h1 className="text-3xl font-bold text-stone-800 mb-4">Installation Failed</h1>
      <p className="text-stone-600 mb-8">We couldn't complete the installation. Error: {error}</p>
      <Link
        to="/"
        className={`inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-full font-semibold hover:bg-brand-orange-hover transition-colors ${focusRing}`}
      >
        Try again
        <RefreshCw className="w-4 h-4" />
      </Link>
    </div>
  );
}

function CancelledContent() {
  return (
    <div className="max-w-md w-full bg-white rounded-3xl border border-orange-200 shadow-sm p-8 text-center">
      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <XCircle className="w-8 h-8 text-brand-orange" />
      </div>
      <h1 className="text-3xl font-bold text-stone-800 mb-4">Installation Cancelled</h1>
      <p className="text-stone-600 mb-8">You cancelled the Kyubot installation.</p>
      <Link
        to="/"
        className={`inline-flex items-center gap-2 text-stone-700 hover:text-brand-orange font-semibold transition-colors ${focusRing}`}
      >
        Return to homepage
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function SuccessContent({
  status,
  team,
  appId,
  trialDays,
  hasPaidAccess,
  teamId,
  preferAnnual,
}: {
  status: WelcomeStatus;
  team: string;
  appId: string;
  trialDays: number;
  hasPaidAccess: boolean;
  teamId: string;
  preferAnnual: boolean;
}) {
  const isReinstall = status === "reinstall";
  const trialJustStarted = status === "trial_started";
  const title = isReinstall ? "Welcome back!" : "You're all set!";

  return (
    <div className="max-w-lg w-full space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-3xl border border-orange-200 shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-emerald-700" />
        </div>
        <h1 className="text-3xl font-bold text-stone-800 mb-2">{title}</h1>
        <p className="text-lg text-stone-600">
          {isReinstall ? "Kyubot has been reconnected to " : "Kyubot has been added to "}
          <strong className="text-brand-orange">{team}</strong>
        </p>
      </div>

      {/* Quick Start - only show for new installs */}
      {!isReinstall && (
        <div className="bg-white rounded-3xl border border-orange-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-4">
            Quick Start
          </h3>
          <div className="space-y-3">
            {[
              { cmd: "!add", desc: "Add an item to the queue" },
              { cmd: "!list", desc: "See all queued items" },
              { cmd: "!done", desc: "Mark an item as complete" },
              { cmd: "!help", desc: "See all commands" },
            ].map((item) => (
              <div key={item.cmd} className="flex items-center gap-4">
                <code className="bg-orange-50 text-brand-orange px-3 py-1.5 rounded-lg text-sm font-mono font-semibold border border-orange-200 min-w-[70px] text-center">
                  {item.cmd}
                </code>
                <span className="text-stone-600">{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open Slack button */}
      <div className="text-center">
        <a
          href={appId ? `https://slack.com/app_redirect?app=${appId}` : "slack://open"}
          className={`inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-brand-orange-hover transition-all shadow-lg shadow-brand-orange/20 ${focusRing}`}
        >
          <ExternalLink className="w-5 h-5" />
          Open Slack
        </a>
      </div>

      {/* Trial Started Banner */}
      {trialJustStarted && (
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-3xl p-6 text-white text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <PartyPopper className="w-5 h-5" />
            <h2 className="text-xl font-bold">Your Pro Trial is Active!</h2>
          </div>
          <p className="text-emerald-100 mb-4">
            You have {trialDays} days of unlimited queues and items. Enjoy!
          </p>
          {teamId && (
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href={`${API_BASE_URL}/api/upgrade?plan=${preferAnnual ? "annual" : "monthly"}&team=${teamId}`}
                className={`bg-white text-emerald-700 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors ${focusRing}`}
              >
                {preferAnnual ? "Subscribe Annual (Save 17%)" : "Subscribe Monthly"}
              </a>
              <a
                href={`${API_BASE_URL}/api/upgrade?plan=${preferAnnual ? "monthly" : "annual"}&team=${teamId}`}
                className={`bg-white/20 text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-white/30 transition-colors border border-white/30 ${focusRing}`}
              >
                {preferAnnual ? "Monthly" : "Annual (Save 17%)"}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Upgrade Section - only show if not on paid plan and trial didn't just start */}
      {!hasPaidAccess && !trialJustStarted && teamId && (
        <div className="bg-gradient-to-r from-brand-orange to-brand-orange-hover rounded-3xl p-6 text-white">
          <h2 className="text-xl font-bold mb-2">Unlock Pro Features</h2>
          <p className="text-white/80 mb-6">
            {trialDays === 30
              ? "Get unlimited queues and items with a 30-day free trial."
              : trialDays > 0
                ? `Continue your trial with ${trialDays} days remaining.`
                : "Upgrade to Pro for unlimited queues and items."}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={trialDays > 0 ? `${API_BASE_URL}/api/start-trial?team=${teamId}` : `${API_BASE_URL}/api/upgrade?plan=${preferAnnual ? "annual" : "monthly"}&team=${teamId}`}
              className={`bg-white text-stone-900 px-6 py-3 rounded-full font-semibold hover:bg-orange-50 transition-colors ${focusRing}`}
            >
              {trialDays > 0 ? "Start Free Trial" : preferAnnual ? "Subscribe Annual (Save 17%)" : "Subscribe Monthly"}
            </a>
            {trialDays <= 0 && (
              <a
                href={`${API_BASE_URL}/api/upgrade?plan=${preferAnnual ? "monthly" : "annual"}&team=${teamId}`}
                className={`bg-white/30 text-white px-6 py-3 rounded-full font-semibold hover:bg-white/40 transition-colors border border-white/40 ${focusRing}`}
              >
                {preferAnnual ? "Monthly" : "Annual (Save 17%)"}
              </a>
            )}
          </div>
          {trialDays > 0 && (
            <p className="text-sm text-white/80 mt-4 text-center">
              No credit card required. Or type{" "}
              <code className="bg-white/20 px-2 py-0.5 rounded">!upgrade trial</code> in Slack
              anytime.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default Welcome;
